import { BookText, FolderPlusIcon, RefreshCwIcon, CreditCard } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { UploadIcon } from '@radix-ui/react-icons';
import DialogFormCreateFolder from './DialogFormCreateFolder';
import DialogUploadFile from './DialogUploadFile';
import { cn } from '@/lib/utils';
import { useUploadStore } from '@/store/uploadStore';
import { ExtendedFile } from '@/components/ui/file-uploader';
import { Link, useParams } from 'react-router-dom';
import { RouteParams } from '@/router/type';
import { useUploadFileOnRemote } from '@/service/serverless-gpu/hook';
import path from 'path-browserify';
import axios from 'axios';
import { calculateUploadSpeed } from '@/utils/uploadUtils';
import { PATH_DOCS } from '@/constants';
import { useGetUserBalance } from '@/service/payment/hook';
import { useQuickTopup } from '@/hooks/useQuickTopup';
import { QuickTopupDialog } from '@/components/quick-topup/QuickTopupDialog';

type ToolbarActionStorageProps = {
  currentPath: string[];
  handleRefresh: () => Promise<void>;
  loadingRefresh: boolean;
};

const ToolbarActionStorage = ({
  currentPath,
  handleRefresh,
  loadingRefresh,
}: ToolbarActionStorageProps) => {
  const { projectId } = useParams<RouteParams>();
  const { mutateAsync: uploadFile } = useUploadFileOnRemote();
  const {
    addFiles,
    updateProgress,
    updateSpeed,
    updateStatus,
    setController,
    generateFileKey,
  } = useUploadStore();

  // Credit balance check
  const { data: creditData } = useGetUserBalance();
  const hasInsufficientBalance = (creditData?.balance ?? 0) < 0.5;
  const { isOpen, setIsOpen, openTopup, onSuccess } = useQuickTopup();

  const handleUploadFile = async (params: {
    files: File[];
    uploadPath: string;
  }) => {
    const resultFiles = params.files as ExtendedFile[];

    // เพิ่มไฟล์ทั้งหมดเข้า store ตั้งแต่เริ่มต้น
    addFiles(resultFiles, params.uploadPath);

    try {
      const { uploads } = await uploadFile({
        project_id: projectId!,
        file_list: resultFiles.map((file) => {
          const filePath = file.path.startsWith('/')
            ? file.path.substring(1)
            : file.path;

          const fileName = path.basename(filePath);

          const directoryPath = path.dirname(filePath);

          const targetPath =
            directoryPath === '.'
              ? params.uploadPath || ''
              : path.join(params.uploadPath || '', directoryPath);

          return {
            file_name: fileName,
            file_path: targetPath,
          };
        }),
      });

      // Upload files sequentially
      const failedUploads = [];

      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        const file = resultFiles[i];

        // ใช้ path จาก file โดยตรง
        const filePath = file.path || '';

        // สร้าง unique key
        const fileKey = generateFileKey(file, filePath);

        // สร้าง AbortController สำหรับแต่ละไฟล์
        const abortController = new AbortController();
        setController(fileKey, abortController);

        // เก็บเวลาเริ่มต้นและข้อมูลที่อัพโหลดไปแล้ว
        const startTime = Date.now();
        let lastLoaded = 0;
        let lastTime = startTime;

        try {
          // อัพเดทสถานะเป็น uploading
          updateStatus(fileKey, 'uploading');

          const response = await axios.put(upload.url, file, {
            headers: {
              'Content-Type': file.type,
            },
            // Prevent axios from automatically transforming the data
            transformRequest: [(data) => data],
            // Add configuration for large file uploads
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            // เพิ่ม signal จาก AbortController
            signal: abortController.signal,
            // Add progress tracking
            onUploadProgress: (progressEvent) => {
              const currentTime = Date.now();
              const loaded = progressEvent.loaded;
              const total = progressEvent.total || file.size;

              // คำนวณความเร็วอัพโหลด
              const timeElapsed = (currentTime - lastTime) / 1000;
              if (timeElapsed > 0.5) {
                const { speedText } = calculateUploadSpeed(
                  loaded,
                  lastLoaded,
                  timeElapsed
                );

                // อัพเดทความเร็วใน store
                updateSpeed(fileKey, speedText);

                lastLoaded = loaded;
                lastTime = currentTime;
              }

              const percentCompleted = Math.round((loaded * 100) / total);

              // อัพเดทความคืบหน้าใน store
              updateProgress(fileKey, percentCompleted);

              // ถ้าเสร็จสิ้นแล้ว (100%) ให้อัพเดทสถานะเป็น completed
              if (percentCompleted === 100) {
                updateStatus(fileKey, 'completed');
              }
            },
          });

          if (response.status !== 200) {
            throw new Error(
              `Failed to upload file ${file.name}: ${response.statusText}`
            );
          }

          // อัพเดทสถานะเป็น completed อีกครั้งเพื่อความแน่ใจ
          updateStatus(fileKey, 'completed');
        } catch (error) {
          if (axios.isCancel(error)) {
            console.log(`Upload of ${file.name} was cancelled`);
            // สถานะถูกอัพเดทเป็น cancelled ใน cancelUpload แล้ว
          } else {
            console.error(`Failed to upload file ${file.name}:`, error);
            // อัพเดทสถานะเป็น error
            const errorMessage =
              error instanceof Error ? error.message : 'Unknown error occurred';
            updateStatus(fileKey, 'error', errorMessage);
            failedUploads.push({ file: file.name, error });
          }
        }
      }

      // Check results
      if (failedUploads.length > 0) {
        console.error('Some files failed to upload:', failedUploads);
        throw new Error(`Failed to upload ${failedUploads.length} file(s)`);
      }
      handleRefresh();
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  };

  return (
    <div>
      <div className="flex justify-end gap-2">
        {/* <DialogFormCreateFolder currentPath={currentPath}>
          <Button
            variant="outline"
            startIcon={<FolderPlusIcon className="h-4 w-4" />}
          >
            Create Folder
          </Button>
        </DialogFormCreateFolder> */}
        <Link to={PATH_DOCS.storage} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size={'default'} >
            <BookText className={cn('h-4 w-4')} />
          </Button>
        </Link>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={loadingRefresh}
          size={'default'}
        >
          {/* Refresh */}
          <RefreshCwIcon
            className={cn('h-4 w-4', loadingRefresh && 'animate-spin')}
          />
        </Button>
        {hasInsufficientBalance ? (
          <Button
            startIcon={<CreditCard className="h-4 w-4" />}
            onClick={openTopup}
          >
            <span>Topup & Upload</span>
          </Button>
        ) : (
          <DialogUploadFile
            currentPath={currentPath}
            handleUploadFile={handleUploadFile}
          >
            <Button
              startIcon={<UploadIcon className="h-4 w-4" />}
              disabled={loadingRefresh}
            >
              <span>Upload</span>
            </Button>
          </DialogUploadFile>
        )}
      </div>

      <QuickTopupDialog open={isOpen} onOpenChange={setIsOpen} onSuccess={onSuccess} />
    </div>
  );
};

export default ToolbarActionStorage;
